import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import {
  Trash2,
  Search,
  Filter,
  HardDrive,
  AlertTriangle,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  Calendar,
  Layers,
  ArrowUpDown,
  CheckSquare,
  Square
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";

const DataCleanManager = () => {
  const { user } = useContext(AuthContext);
  const { whatsApps: userWhatsApps } = useWhatsApps();

  const [activeTab, setActiveTab] = useState("chats");
  const [searchParam, setSearchParam] = useState("");
  const [whatsappId, setWhatsappId] = useState("");

  // Chats Tab States
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);

  // Media Tab States
  const [media, setMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaType, setMediaType] = useState("");
  const [orderBy, setOrderBy] = useState("date");
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const fetchChats = async () => {
    setLoadingChats(true);
    try {
      const { data } = await api.get("/data-clean/chats", {
        params: { whatsappId, searchParam }
      });
      setChats(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingChats(false);
    }
  };

  const fetchMedia = async () => {
    setLoadingMedia(true);
    try {
      const { data } = await api.get("/data-clean/media", {
        params: { whatsappId, searchParam, mediaType, orderBy }
      });
      setMedia(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMedia(false);
    }
  };

  useEffect(() => {
    if (activeTab === "chats") {
      fetchChats();
    } else {
      fetchMedia();
      setSelectedMediaIds([]);
    }
  }, [activeTab, whatsappId, mediaType, orderBy]);

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      if (activeTab === "chats") {
        fetchChats();
      } else {
        fetchMedia();
      }
    }
  };

  const handleDeleteChat = async (ticketId, contactName) => {
    if (!window.confirm(`¿Estás absolutamente seguro de eliminar permanentemente todos los chats y archivos asociados al contacto ${contactName}? Esta acción no se puede deshacer.`)) return;

    try {
      await api.delete(`/data-clean/chats/${ticketId}`);
      toast.success(`Chat de ${contactName} eliminado con éxito.`);
      fetchChats();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteMedia = async (messageId) => {
    if (!window.confirm("¿Estás seguro de eliminar permanentemente este archivo multimedia del servidor?")) return;

    try {
      await api.delete(`/data-clean/media/${messageId}`);
      toast.success("Archivo eliminado con éxito.");
      fetchMedia();
    } catch (err) {
      toastError(err);
    }
  };

  const handleBulkDeleteMedia = async () => {
    if (selectedMediaIds.length === 0) return;
    if (!window.confirm(`¿Estás seguro de eliminar permanentemente los ${selectedMediaIds.length} archivos multimedia seleccionados?`)) return;

    try {
      await api.post("/data-clean/media/bulk-delete", { messageIds: selectedMediaIds });
      toast.success(`${selectedMediaIds.length} archivos eliminados con éxito.`);
      setSelectedMediaIds([]);
      fetchMedia();
    } catch (err) {
      toastError(err);
    }
  };

  const toggleSelectMedia = (id) => {
    setSelectedMediaIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAllMedia = () => {
    if (selectedMediaIds.length === media.length) {
      setSelectedMediaIds([]);
    } else {
      setSelectedMediaIds(media.map(m => m.id));
    }
  };

  const renderMediaPreview = (item) => {
    if (item.mediaType === "image") {
      return (
        <img
          src={item.mediaUrl}
          alt={item.body}
          className="w-full h-32 object-cover rounded-lg border border-slate-100 dark:border-slate-800"
        />
      );
    } else if (item.mediaType === "video") {
      return (
        <div className="relative w-full h-32 bg-slate-900 rounded-lg flex items-center justify-center border border-slate-100 dark:border-slate-800 overflow-hidden">
          <video src={item.mediaUrl} className="absolute inset-0 w-full h-full object-cover opacity-60" muted />
          <Video className="w-8 h-8 text-white relative z-10" />
        </div>
      );
    } else if (item.mediaType === "audio" || item.mediaType === "ptt") {
      return (
        <div className="w-full h-32 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-lg flex flex-col items-center justify-center border border-slate-100 dark:border-slate-800 gap-2">
          <Music className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
          <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold uppercase tracking-wider">Audio</span>
        </div>
      );
    } else {
      return (
        <div className="w-full h-32 bg-slate-50 dark:bg-slate-800/40 rounded-lg flex flex-col items-center justify-center border border-slate-100 dark:border-slate-800 gap-2">
          <FileText className="w-8 h-8 text-slate-500 dark:text-slate-400" />
          <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">Documento</span>
        </div>
      );
    }
  };

  return (
    <div className="p-6 max-w-screen-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-5">
        <div>
          <h1 className="text-3xl font-extrabold bg-gradient-to-r from-rose-600 via-rose-500 to-indigo-600 bg-clip-text text-transparent tracking-tight">
            Administrador de Almacenamiento
          </h1>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">
            Inspecciona el almacenamiento consumido en base de datos y disco, y libera espacio eliminando chats o archivos específicos
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center">
            <HardDrive className="w-5 h-5 text-rose-600 dark:text-rose-400" />
          </div>
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            Limpieza y Control
          </span>
        </div>
      </div>

      {/* Tabs & General Filters */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/80 shadow-sm">
        {/* Tabs */}
        <div className="flex bg-slate-50 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-100 dark:border-slate-800/60 self-start">
          <button
            onClick={() => setActiveTab("chats")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === "chats"
                ? "bg-white dark:bg-slate-700 text-rose-600 dark:text-rose-400 shadow-sm"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            Chats / Conversaciones
          </button>
          <button
            onClick={() => setActiveTab("media")}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === "media"
                ? "bg-white dark:bg-slate-700 text-rose-600 dark:text-rose-400 shadow-sm"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            Archivos Multimedia
          </button>
        </div>

        {/* Global Controls & Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 max-w-2xl">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar..."
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 dark:border-slate-750 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          </div>

          {/* Connection */}
          <select
            value={whatsappId}
            onChange={(e) => setWhatsappId(e.target.value)}
            className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-750 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
          >
            <option value="">Todas las conexiones</option>
            {userWhatsApps.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>

          {/* Tab Specific Filters */}
          {activeTab === "media" ? (
            <select
              value={mediaType}
              onChange={(e) => setMediaType(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-750 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
            >
              <option value="">Todos los archivos</option>
              <option value="image">Imágenes</option>
              <option value="video">Videos</option>
              <option value="audio">Audios</option>
              <option value="document">Documentos</option>
            </select>
          ) : (
            <button
              onClick={fetchChats}
              className="w-full py-2 bg-rose-600 text-white rounded-xl text-xs font-bold hover:bg-rose-700 transition-colors shadow-sm"
            >
              Aplicar Búsqueda
            </button>
          )}
        </div>
      </div>

      {/* Tab Specific Sort (For Media) */}
      {activeTab === "media" && (
        <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-50 dark:bg-slate-900/40 p-4 rounded-xl border border-slate-100 dark:border-slate-850">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSelectAllMedia}
              className="flex items-center gap-1.5 text-xs text-slate-650 dark:text-slate-350 hover:text-slate-900 font-bold"
            >
              {selectedMediaIds.length === media.length && media.length > 0 ? (
                <CheckSquare className="w-4 h-4 text-rose-500" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {selectedMediaIds.length === media.length && media.length > 0
                ? "Deseleccionar todos"
                : "Seleccionar todos"}
            </button>

            {selectedMediaIds.length > 0 && (
              <button
                onClick={handleBulkDeleteMedia}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 border border-rose-100 dark:border-rose-900/40 rounded-lg hover:bg-rose-100 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Eliminar Seleccionados ({selectedMediaIds.length})
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
              <ArrowUpDown className="w-3 h-3" /> Ordenar por:
            </span>
            <select
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value)}
              className="px-2.5 py-1 text-xs border border-slate-200 dark:border-slate-750 bg-white dark:bg-slate-850 text-slate-800 dark:text-slate-100 rounded-lg focus:ring-1 focus:ring-rose-500 transition-all font-semibold"
            >
              <option value="date">Más recientes</option>
              <option value="size">Mayor tamaño (peso)</option>
            </select>
          </div>
        </div>
      )}

      {/* Warnings & Alerts */}
      <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-550/20 rounded-2xl p-4 text-xs text-amber-700 dark:text-amber-400 font-medium">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <div>
          <span className="font-bold text-amber-800 dark:text-amber-350 block mb-0.5">Advertencia de Seguridad</span>
          La eliminación de datos es definitiva. La purga de chats eliminará los mensajes correspondientes de la base de datos de manera permanente. La purga de multimedia liberará espacio físico en el disco del servidor de forma irreversible.
        </div>
      </div>

      {/* Main Content Area */}
      {activeTab === "chats" ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800/80 shadow-sm overflow-hidden">
          {loadingChats ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <div className="w-10 h-10 border-4 border-rose-600/20 border-t-rose-600 rounded-full animate-spin mb-3" />
              <p className="text-xs font-semibold">Cargando chats...</p>
            </div>
          ) : chats.length === 0 ? (
            <div className="text-center py-20 text-slate-400 dark:text-slate-500 text-sm font-semibold">
              No se encontraron chats con los filtros seleccionados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[650px] w-full divide-y divide-slate-100 dark:divide-slate-800/50 text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/30 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-4 whitespace-nowrap">Contacto / Número</th>
                    <th className="px-6 py-4 whitespace-nowrap">Conexión</th>
                    <th className="px-6 py-4 text-center whitespace-nowrap">Mensajes Totales</th>
                    <th className="px-6 py-4 text-center whitespace-nowrap">Peso de Textos (DB)</th>
                    <th className="px-6 py-4 text-right whitespace-nowrap">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/30 text-slate-700 dark:text-slate-350 font-medium">
                  {chats.map((chat) => (
                    <tr
                      key={chat.id}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-800/10 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">
                          {chat.contactName || "Contacto Desconocido"}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{chat.contactNumber}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-500 dark:text-slate-400">{chat.whatsappName || "Desconectado"}</td>
                      <td className="px-6 py-4 text-center font-bold">{chat.messageCount}</td>
                      <td className="px-6 py-4 text-center font-bold text-rose-600 dark:text-rose-400">
                        {formatBytes(chat.size)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDeleteChat(chat.id, chat.contactName)}
                          className="p-2 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-lg transition-colors"
                          title="Eliminar todos los mensajes de este chat"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Media Grid */
        <div>
          {loadingMedia ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div className="w-10 h-10 border-4 border-rose-600/20 border-t-rose-600 rounded-full animate-spin mb-3" />
              <p className="text-xs font-semibold">Cargando archivos multimedia...</p>
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-20 text-slate-400 dark:text-slate-500 text-sm font-semibold bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
              No se encontraron archivos multimedia con los filtros seleccionados.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {media.map((item) => {
                const isSelected = selectedMediaIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={`relative rounded-2xl bg-white dark:bg-slate-900 border transition-all overflow-hidden group shadow-sm flex flex-col justify-between ${
                      isSelected
                        ? "border-rose-500 ring-2 ring-rose-500/20"
                        : "border-slate-150 dark:border-slate-800 hover:border-slate-250 dark:hover:border-slate-700 hover:shadow-md"
                    }`}
                  >
                    {/* Selection Checkbox */}
                    <button
                      onClick={() => toggleSelectMedia(item.id)}
                      className="absolute top-2.5 left-2.5 z-20 p-1 bg-white/95 dark:bg-slate-900/95 shadow-sm rounded-lg text-slate-500 border border-slate-100 dark:border-slate-850"
                    >
                      {isSelected ? (
                        <CheckSquare className="w-4 h-4 text-rose-500" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>

                    {/* Preview */}
                    <div className="p-2.5 pb-0 relative">{renderMediaPreview(item)}</div>

                    {/* Info */}
                    <div className="p-3 space-y-1.5 flex-1 flex flex-col justify-between">
                      <div className="space-y-1">
                        <div className="font-bold text-xs text-slate-850 dark:text-slate-200 line-clamp-1">
                          {item.contactName || "Contacto"}
                        </div>
                        <div className="text-[10px] text-slate-400 font-semibold truncate">
                          {item.rawMediaUrl}
                        </div>
                      </div>

                      <div className="pt-2 border-t border-slate-50 dark:border-slate-850 flex items-end justify-between">
                        <div>
                          <div className="text-[10px] font-extrabold text-rose-600 dark:text-rose-400">
                            {formatBytes(item.size)}
                          </div>
                          <div className="text-[9px] text-slate-400 flex items-center gap-0.5 mt-0.5 font-medium">
                            <Calendar className="w-2.5 h-2.5" />
                            {new Date(item.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteMedia(item.id)}
                          className="p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataCleanManager;
